import { AUTH_MODES } from '../constants/auth';

/**
 * Filter the Azure/Microsoft methods out of the normalized discovery list
 * (`DiscoveredAuthMethod[]` from @vulog/auth's `parseDiscoveryBody`, where the
 * IDP `AZURE_AD` type is mapped to `mode: 'microsoft'`).
 * @param {Array<{ mode: string|null, urls: Object }>} authMethods - normalized methods
 * Returns a list (a tenant may advertise several Azure tenants), where
 * `getOAuth2Method` returns a single method — a tenant has at most one SSO.
 * @returns {Array<{ mode: string|null, urls: Object, name: string }>} the Microsoft methods
 */
export const getAzureMethods = authMethods =>
    (Array.isArray(authMethods) ? authMethods : []).filter(({ mode }) => mode === AUTH_MODES.MICROSOFT);

/**
 * Find the OAuth2 (PKCE) method in the normalized discovery list (mode
 * `'oauth2'`, mapped by @vulog/auth's `parseDiscoveryBody` from the `OAUTH2`
 * IDP type).
 * @param {Array<{ mode: string|null, urls: Object }>} authMethods - normalized methods
 * @returns {{ mode: string|null, urls: Object, name: string }|undefined} the OAuth2 method, or undefined
 */
export const getOAuth2Method = authMethods =>
    (Array.isArray(authMethods) ? authMethods : []).find(({ mode }) => mode === AUTH_MODES.OAUTH2);

/**
 * Whether the username/password form should be offered, from the normalized
 * discovery list (`mode === 'ropc'`, mapped by @vulog/auth's
 * `parseDiscoveryBody` from the `VULOG` IDP type).
 *
 * Fails open: an empty or missing list means discovery has not run yet, or has
 * failed — `initAzureAuthSaga` logs that and carries on — and hiding every way
 * in would lock the tenant out of its own front end. A tenant that genuinely
 * has no password login answers with its other methods, never with nothing.
 *
 * @param {Array<{ mode: string|null }>} authMethods - normalized methods
 * @returns {boolean} whether to render the password form
 */
export const isPasswordLoginEnabled = authMethods => {
    if (!Array.isArray(authMethods) || authMethods.length === 0) return true;
    return authMethods.some(({ mode }) => mode === AUTH_MODES.ROPC);
};
